import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDEditor from "components/MDEditor";

import { request } from "app/network";

interface AddEditTermModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    darkMode: boolean;
    term?: any;
    termsTypes: Array<{ code: string; name: string }>;
}


interface AddEditTermModalState {
    termsType: string;
    version: string;
    title: string;
    content: string;
    effectiveDate: string;
    isActive: boolean;
    loading: boolean;
    errors: { [key: string]: string };
    editorMode: "visual" | "html" | "preview"; // 에디터 모드 (비주얼, HTML 소스, 또는 미리보기)
}

class AddEditTermModal extends Component<AddEditTermModalProps, AddEditTermModalState> {
    constructor(props: AddEditTermModalProps) {
        super(props);

        const term = props.term;
        this.state = {
            termsType: term?.termsType || "",
            version: term?.version || "",
            title: term?.title || "",
            content: term?.content || "",
            effectiveDate: term?.effectiveDate || new Date().toISOString().split('T')[0],
            isActive: term?.isActive ?? true,
            loading: false,
            errors: {},
            editorMode: "visual",
        };
    }

    componentDidUpdate(prevProps: AddEditTermModalProps) {
        if (this.props.term !== prevProps.term && this.props.open) {
            const term = this.props.term;
            this.setState({
                termsType: term?.termsType || "",
                version: term?.version || "",
                title: term?.title || "",
                content: term?.content || "",
                effectiveDate: term?.effectiveDate || new Date().toISOString().split('T')[0],
                isActive: term?.isActive ?? true,
                errors: {},
            });
        }
    }

    validateForm = (): boolean => {
        const errors: { [key: string]: string } = {};

        if (!this.state.termsType) errors.termsType = "약관 유형을 선택해주세요.";
        if (!this.state.version.trim()) errors.version = "버전을 입력해주세요.";
        if (!this.state.title.trim()) errors.title = "제목을 입력해주세요.";
        if (!this.state.content.trim() || this.state.content === '<p><br></p>') errors.content = "내용을 입력해주세요.";
        if (!this.state.effectiveDate) errors.effectiveDate = "시행일을 선택해주세요.";

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    handleSave = async () => {
        if (!this.validateForm()) return;

        try {
            this.setState({ loading: true });

            const payload = {
                TermsType: this.state.termsType,
                Version: this.state.version,
                Title: this.state.title,
                Content: this.state.content,
                EffectiveDate: this.state.effectiveDate,
                IsActive: this.state.isActive,
            };

            if (this.props.term) {
                // 수정
                await request("LegalTerms.update", {
                    pathParams: { id: this.props.term.termsSeqId },
                    paramz: payload,
                });
            } else {
                // 추가
                await request("LegalTerms.create", { paramz: payload });
            }

            this.setState({ loading: false });
            this.props.onSuccess();
            this.handleClose();
        } catch (error: any) {
            console.error("약관 저장 실패:", error);
            this.setState({ loading: false });

            // 백엔드 유효성 검사 에러 표시
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => `${key}: ${errors[key].join(', ')}`).join('\n');
                alert(`유효성 검사 실패:\n${errorMessages}`);
            } else if (error.response?.data?.message) {
                alert(error.response.data.message);
            } else {
                alert("약관 저장에 실패했습니다.");
            }
        }
    };

    handleClose = () => {
        this.setState({
            termsType: "",
            version: "",
            title: "",
            content: "",
            effectiveDate: new Date().toISOString().split('T')[0],
            isActive: true,
            errors: {},
        });
        this.props.onClose();
    };

    render() {
        const { open, darkMode, termsTypes } = this.props;
        const { termsType, version, title, content, effectiveDate, isActive, loading, errors } = this.state;
        const isEdit = !!this.props.term;

        return (
            <Dialog
                open={open}
                onClose={this.handleClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#1a2035" : "#fff",
                        color: darkMode ? "#fff" : "#000",
                        maxHeight: "90vh",
                    },
                }}
            >
                <DialogTitle>
                    <MDTypography variant="h5" fontWeight="medium" component="span">
                        {isEdit ? "약관 수정" : "약관 추가"}
                    </MDTypography>
                </DialogTitle>

                <DialogContent>
                    <MDBox pt={2}>
                        <Grid container spacing={3}>
                            {/* 약관 유형 */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={!!errors.termsType} size="small">
                                    <InputLabel sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                        약관 유형 *
                                    </InputLabel>
                                    <Select
                                        value={termsType}
                                        label="약관 유형 *"
                                        onChange={(e) => this.setState({ termsType: e.target.value, errors: { ...errors, termsType: "" } })}
                                        sx={{
                                            height: '40px',
                                            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                                            color: darkMode ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: errors.termsType ? '#f44336' : (darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'),
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
                                            },
                                        }}
                                    >
                                        {termsTypes.map((type) => (
                                            <MenuItem key={type.code} value={type.code}>
                                                {type.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.termsType && (
                                        <MDTypography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            {errors.termsType}
                                        </MDTypography>
                                    )}
                                </FormControl>
                            </Grid>

                            {/* 버전 */}
                            <Grid item xs={12} md={6}>
                                <MDInput
                                    label="버전 *"
                                    fullWidth
                                    value={version}
                                    onChange={(e: any) => this.setState({ version: e.target.value, errors: { ...errors, version: "" } })}
                                    error={!!errors.version}
                                    helperText={errors.version}
                                    placeholder="예: 1.0.0"
                                />
                            </Grid>

                            {/* 제목 */}
                            <Grid item xs={12}>
                                <MDInput
                                    label="제목 *"
                                    fullWidth
                                    value={title}
                                    onChange={(e: any) => this.setState({ title: e.target.value, errors: { ...errors, title: "" } })}
                                    error={!!errors.title}
                                    helperText={errors.title}
                                />
                            </Grid>

                            {/* 시행일 */}
                            <Grid item xs={12} md={6}>
                                <MDInput
                                    type="date"
                                    label="시행일 *"
                                    fullWidth
                                    value={effectiveDate}
                                    onChange={(e: any) => this.setState({ effectiveDate: e.target.value, errors: { ...errors, effectiveDate: "" } })}
                                    error={!!errors.effectiveDate}
                                    helperText={errors.effectiveDate}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: darkMode ? 'invert(1)' : 'none',
                                            cursor: 'pointer',
                                        },
                                    }}
                                />
                            </Grid>

                            {/* 활성 상태 */}
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isActive}
                                            onChange={(e) => this.setState({ isActive: e.target.checked })}
                                        />
                                    }
                                    label="활성화"
                                />
                            </Grid>

                            {/* 내용 (HTML 편집기) */}
                            <Grid item xs={12}>
                                <MDBox mb={1} display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="caption" fontWeight="medium">
                                        내용 *
                                    </MDTypography>
                                    <Tabs
                                        value={this.state.editorMode}
                                        onChange={(_, value) => this.setState({ editorMode: value })}
                                        sx={{
                                            minHeight: "32px",
                                            "& .MuiTab-root": {
                                                minHeight: "32px",
                                                padding: "4px 12px",
                                                fontSize: "0.75rem",
                                            },
                                        }}
                                    >
                                        <Tab label="비주얼 에디터" value="visual" />
                                        <Tab label="HTML 소스" value="html" />
                                        <Tab label="HTML 미리보기" value="preview" />
                                    </Tabs>
                                </MDBox>

                                {this.state.editorMode === "visual" ? (
                                    <MDEditor
                                        value={content}
                                        onChange={(value: string) => this.setState({ content: value, errors: { ...errors, content: "" } })}
                                        style={{ height: "550px" }}
                                    />
                                ) : this.state.editorMode === "html" ? (
                                    <MDInput
                                        multiline
                                        rows={25}
                                        value={content}
                                        onChange={(e: any) => this.setState({ content: e.target.value, errors: { ...errors, content: "" } })}
                                        fullWidth
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                fontFamily: "monospace",
                                                fontSize: "0.875rem",
                                            },
                                        }}
                                    />
                                ) : (
                                    <MDBox
                                        sx={{
                                            border: "1px solid",
                                            borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                            borderRadius: "8px",
                                            padding: "16px",
                                            minHeight: "550px",
                                            maxHeight: "550px",
                                            overflowY: "auto",
                                            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                                            "& img": {
                                                maxWidth: "100%",
                                                height: "auto",
                                            },
                                            "& table": {
                                                borderCollapse: "collapse",
                                                width: "100%",
                                            },
                                            "& th, & td": {
                                                border: "1px solid",
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                                padding: "8px",
                                            },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: content }}
                                    />
                                )}

                                {errors.content && (
                                    <MDTypography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                                        {errors.content}
                                    </MDTypography>
                                )}
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <MDButton onClick={this.handleClose} variant="outlined" color="secondary" disabled={loading}>
                        취소
                    </MDButton>
                    <MDButton onClick={this.handleSave} variant="gradient" color="info" disabled={loading}>
                        {loading ? "저장 중..." : "저장"}
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

export default AddEditTermModal;
